/**
 * Color Role - Types
 *
 * Tipos para representação de cores nas diferentes variantes.
 */

// =============================================================================
// BASE TYPE
// =============================================================================

/**
 * Tipo base para Color: objeto RGBA com valores 0-255 e alpha 0-1
 *
 * @example
 * const red: RGBA = { r: 255, g: 0, b: 0, a: 1 };
 * const semiTransparent: RGBA = { r: 0, g: 0, b: 255, a: 0.5 };
 */
export interface RGBA {
  r: number; // 0-255
  g: number; // 0-255
  b: number; // 0-255
  a: number; // 0-1
}

// =============================================================================
// VARIANT TYPES
// =============================================================================

/**
 * Variante Hex
 *
 * @example
 * "#ff0000"     // RGB (6 chars)
 * "#ff0000ff"   // RGBA (8 chars)
 * "#f00"        // RGB shorthand (3 chars)
 * "#f00f"       // RGBA shorthand (4 chars)
 */
export type ColorHex = string;

/**
 * Variante RGB Object
 *
 * @example
 * { r: 255, g: 0, b: 0 }
 * { r: 255, g: 0, b: 0, a: 0.5 }
 */
export interface ColorRgbObject {
  r: number;
  g: number;
  b: number;
  a?: number;
}

/**
 * Variante RGB String
 *
 * @example
 * "rgb(255, 0, 0)"
 * "rgba(255, 0, 0, 0.5)"
 */
export type ColorRgbString = string;

/**
 * Variante HSL Object
 *
 * @example
 * { h: 0, s: 100, l: 50 }        // Red
 * { h: 0, s: 100, l: 50, a: 0.5 } // Semi-transparent red
 */
export interface ColorHslObject {
  h: number; // 0-360
  s: number; // 0-100
  l: number; // 0-100
  a?: number; // 0-1
}

/**
 * Variante HSL String
 *
 * @example
 * "hsl(0, 100%, 50%)"
 * "hsla(0, 100%, 50%, 0.5)"
 */
export type ColorHslString = string;

// =============================================================================
// VARIANT NAMES
// =============================================================================

export type ColorVariant =
  | "hex"
  | "rgb_object"
  | "rgb_string"
  | "hsl_object"
  | "hsl_string";

// =============================================================================
// FORMAT OPTIONS
// =============================================================================

import type { BaseFormatOptions } from "../../types";

export interface ColorFormatOptions extends BaseFormatOptions {
  /** Se true, usa letras maiúsculas para hex */
  uppercase?: boolean;

  /** Se true, inclui alpha mesmo quando é 1 */
  includeAlpha?: boolean;

  /** Se true, usa formato compacto (shorthand hex, sem espaços em rgb) */
  compact?: boolean;
}

// =============================================================================
// NAMED COLORS
// =============================================================================

/**
 * CSS Named Colors mais comuns
 */
export const NAMED_COLORS: Record<string, RGBA> = {
  // Basic colors
  black: { r: 0, g: 0, b: 0, a: 1 },
  white: { r: 255, g: 255, b: 255, a: 1 },
  red: { r: 255, g: 0, b: 0, a: 1 },
  green: { r: 0, g: 128, b: 0, a: 1 },
  blue: { r: 0, g: 0, b: 255, a: 1 },
  yellow: { r: 255, g: 255, b: 0, a: 1 },
  cyan: { r: 0, g: 255, b: 255, a: 1 },
  magenta: { r: 255, g: 0, b: 255, a: 1 },

  // Extended colors
  orange: { r: 255, g: 165, b: 0, a: 1 },
  purple: { r: 128, g: 0, b: 128, a: 1 },
  pink: { r: 255, g: 192, b: 203, a: 1 },
  brown: { r: 165, g: 42, b: 42, a: 1 },
  gray: { r: 128, g: 128, b: 128, a: 1 },
  grey: { r: 128, g: 128, b: 128, a: 1 },

  // Web colors
  lime: { r: 0, g: 255, b: 0, a: 1 },
  aqua: { r: 0, g: 255, b: 255, a: 1 },
  fuchsia: { r: 255, g: 0, b: 255, a: 1 },
  silver: { r: 192, g: 192, b: 192, a: 1 },
  maroon: { r: 128, g: 0, b: 0, a: 1 },
  olive: { r: 128, g: 128, b: 0, a: 1 },
  navy: { r: 0, g: 0, b: 128, a: 1 },
  teal: { r: 0, g: 128, b: 128, a: 1 },

  // Transparent
  transparent: { r: 0, g: 0, b: 0, a: 0 },
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Clamp um valor entre min e max
 */
export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/**
 * Normaliza um canal RGB para 0-255
 */
export function normalizeRgbChannel(value: number): number {
  return clamp(Math.round(value), 0, 255);
}

/**
 * Normaliza alpha para 0-1
 */
export function normalizeAlpha(value: number | undefined): number {
  if (value === undefined) return 1;
  return clamp(value, 0, 1);
}

/**
 * Converte RGB para HSL
 */
export function rgbToHsl(
  r: number,
  g: number,
  b: number,
): { h: number; s: number; l: number } {
  r /= 255;
  g /= 255;
  b /= 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;

  if (max === min) {
    return { h: 0, s: 0, l: l * 100 };
  }

  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

  let h: number;
  switch (max) {
    case r:
      h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
      break;
    case g:
      h = ((b - r) / d + 2) / 6;
      break;
    default:
      h = ((r - g) / d + 4) / 6;
      break;
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
}

/**
 * Converte HSL para RGB
 */
export function hslToRgb(
  h: number,
  s: number,
  l: number,
): { r: number; g: number; b: number } {
  h /= 360;
  s /= 100;
  l /= 100;

  if (s === 0) {
    const gray = Math.round(l * 255);
    return { r: gray, g: gray, b: gray };
  }

  const hue2rgb = (p: number, q: number, t: number): number => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };

  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;

  return {
    r: Math.round(hue2rgb(p, q, h + 1 / 3) * 255),
    g: Math.round(hue2rgb(p, q, h) * 255),
    b: Math.round(hue2rgb(p, q, h - 1 / 3) * 255),
  };
}
